import { z } from "zod";

const MIN_PASSWORD_LENGTH = 8;
const SIMILARITY_MIN_PART_LENGTH = 3;

export const PASSWORD_ERRORS = {
	tooShort: "passwordTooShort",
	allNumeric: "passwordAllNumeric",
	tooSimilar: "passwordTooSimilar",
} as const;

export type PasswordUserAttrs = {
	username?: string | null;
	email?: string | null;
	firstName?: string | null;
	lastName?: string | null;
};

function getAttrParts(attrs: PasswordUserAttrs): string[] {
	const values = [attrs.username, attrs.email, attrs.firstName, attrs.lastName];
	const parts = new Set<string>();
	for (const value of values) {
		if (!value) continue;
		const lower = value.toLowerCase();
		parts.add(lower);
		for (const piece of lower.split(/[\W_]+/)) {
			if (piece.length >= SIMILARITY_MIN_PART_LENGTH) parts.add(piece);
		}
	}
	return [...parts];
}

function isTooSimilar(password: string, attrs: PasswordUserAttrs): boolean {
	const lower = password.toLowerCase();
	for (const part of getAttrParts(attrs)) {
		if (lower === part) return true;
		if (part.length >= SIMILARITY_MIN_PART_LENGTH && lower.includes(part)) {
			return true;
		}
	}
	return false;
}

export function createPasswordSchema(attrs: PasswordUserAttrs = {}) {
	return z.string().superRefine((value, ctx) => {
		if (value.length < MIN_PASSWORD_LENGTH) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: PASSWORD_ERRORS.tooShort,
			});
			return;
		}
		if (/^\d+$/.test(value)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: PASSWORD_ERRORS.allNumeric,
			});
		}
		if (isTooSimilar(value, attrs)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: PASSWORD_ERRORS.tooSimilar,
			});
		}
	});
}

export const loginSchema = z.object({
	username: z.string().min(1, "Pole wymagane"),
	password: z.string().min(1, "Pole wymagane"),
});
export type LoginFormValues = z.infer<typeof loginSchema>;

export const signupSchema = z
	.object({
		username: z.string().min(3, "Min. 3 znaki"),
		email: z.string().email("Nieprawidłowy email"),
		password: z.string(),
		confirmPassword: z.string(),
	})
	.superRefine((data, ctx) => {
		const passwordResult = createPasswordSchema({
			username: data.username,
			email: data.email,
		}).safeParse(data.password);
		if (!passwordResult.success) {
			for (const issue of passwordResult.error.issues) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["password"],
					message: issue.message,
				});
			}
		}
		if (data.password !== data.confirmPassword) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["confirmPassword"],
				message: "Hasła nie są zgodne",
			});
		}
	});
export type SignupFormValues = z.infer<typeof signupSchema>;
